import React from 'react';
import { createPortal } from 'react-dom';

export interface ActionDropdownAction {
  key: string;
  label: string;
  icon: string;
  onClick: () => void | Promise<void>;
  className?: string;
  separateAfter?: boolean;
  isLoading?: boolean;
}

interface ActionDropdownProps {
  record: any;
  isOpen: boolean;
  onToggle: () => void;
  actions: ActionDropdownAction[];
  ariaLabel?: string;
  buttonClassName?: string;
  triggerIcon?: string;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  record,
  isOpen,
  onToggle,
  actions,
  ariaLabel,
  buttonClassName = 'p-1 hover:bg-bg-weak-50 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-primary',
  triggerIcon = 'ri-more-2-line text-icon-soft-400 text-lg',
}) => {
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  }>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 160;
      const dropdownHeight = Math.max(100, actions.length * 40 + 16);

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = viewportWidth - buttonRect.right;

      let position: typeof dropdownPosition = {};

      if (spaceBelow >= dropdownHeight) {
        position.top = buttonRect.bottom + 4;
      } else if (spaceAbove >= dropdownHeight) {
        position.bottom = viewportHeight - buttonRect.top + 4;
      } else {
        position.top = Math.max(4, Math.min(buttonRect.bottom + 4, viewportHeight - dropdownHeight - 4));
      }

      if (spaceRight >= dropdownWidth) {
        position.right = viewportWidth - buttonRect.right;
      } else {
        position.left = buttonRect.left - dropdownWidth;
      }

      setDropdownPosition(position);
    }
  }, [isOpen, actions.length]);

  // Close on outside click / Escape / scroll. Without this the menu stays open
  // (and floats over other UI) when the user clicks elsewhere, since open state
  // lives in the parent. Menu is portaled, so check both the trigger and menu.
  React.useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      onToggle();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onToggle();
    };
    const onScroll = () => onToggle();
    // pointerdown covers mouse + touch + pen (mobile-friendly).
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    // Fixed-positioned menu doesn't follow scroll — close instead of drifting.
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, onToggle]);

  const handleActionClick = async (action: ActionDropdownAction) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await action.onClick();
    } catch (error) {
      console.error(`Error executing action ${action.key}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const defaultAriaLabel =
    ariaLabel || `Actions for ${record?.name || record?.platNomor || 'item'}`;

  return (
    <>
      <div className="sticky right-0 bg-bg-white-0 border-l border-stroke-soft-200 relative">
        <button
          ref={buttonRef}
          className={buttonClassName}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggle(); }
            if (e.key === 'Escape' && isOpen) { e.stopPropagation(); onToggle(); }
          }}
          aria-label={defaultAriaLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <i className={triggerIcon}></i>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed min-w-40 bg-bg-white-0 border border-stroke-soft-200 rounded-md shadow-card-sm z-99999"
            style={dropdownPosition}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-label="Action menu"
          >
            {actions.map((action) => {
              const isActionLoading = isLoading || action.isLoading;
              return (
                <React.Fragment key={action.key}>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm text-text-sub-600 hover:bg-bg-weak-50 flex items-center focus:outline-hidden focus:bg-bg-weak-50 transition-colors duration-150 ${
                      action.className || ''
                    } ${action.separateAfter ? 'border-b border-stroke-soft-200' : ''} ${
                      isActionLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => { if (!isActionLoading) handleActionClick(action); }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !isActionLoading) {
                        e.preventDefault();
                        handleActionClick(action);
                      }
                    }}
                    disabled={isActionLoading}
                    role="menuitem"
                  >
                    {isActionLoading ? (
                      <div className="mr-2 w-4 h-4 flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin opacity-60" />
                      </div>
                    ) : (
                      <i className={`${action.icon} mr-2`}></i>
                    )}
                    {action.label}
                  </button>
                </React.Fragment>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionDropdown;
